import { useState } from 'react';
import { Loader2, AlertTriangle, RefreshCw } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { toast } from 'sonner';
import { diagnosticsAPI } from '../../lib/api';

function getErrorMessage(err) {
  return err?.response?.data?.detail || err?.message || 'Request failed';
}

const STATUS_BADGE = {
  resolved: { className: 'bg-green-600 text-white border-transparent', label: 'resolved' },
  would_resolve_if_owned: { className: 'bg-blue-600 text-white border-transparent', label: 'would resolve if owned' },
  no_match: { className: 'bg-orange-500 text-white border-transparent', label: 'no match' },
};

export function CwcMatchPreviewPanel() {
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState(null);

  const refresh = async () => {
    setLoading(true);
    setResults(null);
    try {
      const { data } = await diagnosticsAPI.cwcMatchPreview();
      setResults(data);
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg">CWC Match Preview</CardTitle>
        <p className="text-sm text-muted-foreground">
          Preview which CWC statement property identities resolve against owned Smoobu properties
          before marking additional properties as owned in the integrations designation.
        </p>
      </CardHeader>
      <CardContent className="space-y-4">
        <Button onClick={refresh} disabled={loading} size="sm">
          {loading ? (
            <Loader2 className="h-4 w-4 animate-spin mr-2" />
          ) : (
            <RefreshCw className="h-4 w-4 mr-2" />
          )}
          Refresh
        </Button>

        {results && (
          <div className="space-y-4">
            {/* Summary */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
              <div className="bg-muted rounded p-3">
                <div className="text-muted-foreground text-xs">Statement identities</div>
                <div className="font-semibold">{results.total_identities}</div>
              </div>
              <div className="bg-muted rounded p-3">
                <div className="text-muted-foreground text-xs">Resolved</div>
                <div className="font-semibold text-green-600">{results.resolved_count}</div>
              </div>
              <div className="bg-muted rounded p-3">
                <div className="text-muted-foreground text-xs">Would resolve if owned</div>
                <div className="font-semibold text-blue-600">{results.would_resolve_count}</div>
              </div>
              <div className="bg-muted rounded p-3">
                <div className="text-muted-foreground text-xs">No match</div>
                <div className="font-semibold text-orange-600">{results.no_match_count}</div>
              </div>
            </div>

            {/* Smoobu context */}
            <div className="text-xs text-muted-foreground">
              {results.owned_smoobu_count} owned / {results.total_smoobu_count} total Smoobu properties
            </div>

            {/* Note (empty state) */}
            {results.note && (
              <div className="text-xs text-muted-foreground bg-muted/50 border rounded p-3">
                <AlertTriangle className="h-3 w-3 inline mr-1 text-orange-500" />
                {results.note}
              </div>
            )}

            {/* Table */}
            {results.rows.length > 0 && (
              <div className="overflow-x-auto">
                <table className="w-full text-xs border">
                  <thead className="bg-muted">
                    <tr>
                      <th className="text-left p-2 border">Statement property</th>
                      <th className="text-left p-2 border">Postcode</th>
                      <th className="text-left p-2 border">Stmts</th>
                      <th className="text-left p-2 border">Current ref</th>
                      <th className="text-left p-2 border">Current source</th>
                      <th className="text-left p-2 border">Best Smoobu match</th>
                      <th className="text-left p-2 border">Match field</th>
                      <th className="text-left p-2 border">Similarity</th>
                      <th className="text-left p-2 border">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {results.rows.map((row, i) => {
                      const badge = STATUS_BADGE[row.status] || STATUS_BADGE.no_match;
                      return (
                        <tr key={i} className="border-t">
                          <td className="p-2 border font-mono">{row.statement_property_name || '(empty)'}</td>
                          <td className="p-2 border font-mono">{row.statement_postcode || '-'}</td>
                          <td className="p-2 border text-center">{row.statement_count}</td>
                          <td className="p-2 border font-mono">{row.current_property_ref || '-'}</td>
                          <td className="p-2 border">{row.current_match_source || '-'}</td>
                          <td className="p-2 border font-mono">
                            {row.best_smoobu_match?.smoobu_name
                              ? `${row.best_smoobu_match.smoobu_name} (${row.best_smoobu_match.smoobu_id})`
                              : '-'}
                          </td>
                          <td className="p-2 border">{row.best_smoobu_match?.match_field || '-'}</td>
                          <td className="p-2 border text-center">
                            {row.best_smoobu_match?.similarity != null
                              ? `${(row.best_smoobu_match.similarity * 100).toFixed(0)}%`
                              : '-'}
                          </td>
                          <td className="p-2 border">
                            <Badge className={badge.className}>{badge.label}</Badge>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
